"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDynamicColumnDefinitions = useDynamicColumnDefinitions;
exports.formatDynamicColumnValue = formatDynamicColumnValue;
exports.mapDynamicColumnsToFilterGroups = mapDynamicColumnsToFilterGroups;
const DynamicColumnConfigProvider_1 = __importDefault(require("../../../ui/features/dynamic-columns/components/DynamicColumnConfigProvider"));
const hooks_1 = require("../../../ui/hooks");
const material_1 = require("@mui/material");
const react_1 = __importStar(require("react"));
const immutable_1 = __importDefault(require("swr/immutable"));
function useDynamicColumnDefinitions(entity, enabled) {
    const dynamicColumnContext = (0, react_1.useContext)(DynamicColumnConfigProvider_1.default);
    const dynamicColumnClient = (0, hooks_1.useDynamicColumnClient)();
    // we use two different swr paths, one for enabled other for disabled dynamic columns
    // this is needed because there might be different instances of tables for same entity, and we need to return properly cached data depending on enabled flag
    return (0, immutable_1.default)(`entityTable/${entity}/dynamicColumns/${enabled}`, async () => {
        if (!enabled) {
            return {
                columnDefs: [],
                handlerMap: new Map(),
            };
        }
        const handlerMap = new Map();
        const columnDefs = await dynamicColumnClient.listColumns({
            entity: entity,
            status: 'active',
        });
        const promises = dynamicColumnContext.columnHandlers
            .filter((handler) => columnDefs.rows.some((col) => handler.name === col.metadata?.columnHandlerName))
            .map((handler) => {
            if (handler) {
                if (handler.loadData) {
                    return handler.loadData().then((data) => {
                        handlerMap.set(handler.name, { handler: handler, data: data });
                    });
                }
                else {
                    handlerMap.set(handler.name, { handler: handler, data: undefined });
                }
            }
            return Promise.resolve();
        });
        await Promise.all(promises);
        return {
            columnDefs: columnDefs.rows,
            handlerMap: handlerMap,
        };
    });
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatDynamicColumnValue(row, columnDef, handler) {
    let formattedValue = row.columns[`_dynamic_${columnDef.name}`];
    if (columnDef.type.endsWith('-array') && handler?.handler.formatArray) {
        formattedValue = handler.handler.formatArray(row.columns[`_dynamic_${columnDef.name}`], handler.data ?? new Map());
    }
    else if (handler?.handler.format) {
        formattedValue = handler.handler.format(row.columns[`_dynamic_${columnDef.name}`], handler.data ?? new Map());
    }
    if (typeof formattedValue === 'string') {
        return (react_1.default.createElement(material_1.Typography, { style: { wordWrap: 'break-word' }, component: 'span' }, formattedValue));
    }
    else {
        return formattedValue;
    }
}
function mapDynamicColumnsToFilterGroups(columnData, initialValues) {
    const filterGroups = [];
    if (columnData) {
        const filterDynamicColumns = columnData.columnDefs.filter((column) => column.metadata?.showInFilters);
        for (const column of filterDynamicColumns) {
            const initialValue = initialValues ? getInitialValue(initialValues, column.name) : undefined;
            filterGroups.push(mapDynamicColumnToFilterGroup(column, columnData.handlerMap, initialValue));
        }
    }
    return filterGroups;
}
function getInitialValue(initialValues, columnName) {
    const values = [];
    flatten(initialValues).forEach((filter) => {
        if ('column' in filter && `_dynamic_${columnName}` === filter.column && 'value' in filter && filter.value) {
            values.push(filter.value);
        }
    });
    return values;
}
function flatten(filter) {
    const flatFilter = [];
    filter.forEach((f) => {
        if ('filter' in f && f.op !== 'relation') {
            flatFilter.push(...flatten(f.filter));
        }
        else {
            flatFilter.push(f);
        }
    });
    return flatFilter;
}
function mapDynamicColumnToFilterGroup(dynamicColumn, dynamicColumnHandlers, initialValues) {
    const columnLabel = dynamicColumn.metadata?.label ?? '';
    const columnName = `_dynamic_${dynamicColumn.name}`;
    const columnType = dynamicColumn.type;
    const filterData = mapHandlerToSelectItems(dynamicColumn, dynamicColumnHandlers.get(dynamicColumn.metadata?.columnHandlerName ?? ''));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialValue = initialValues && initialValues.length > 0 ? initialValues[0] : undefined;
    switch (columnType) {
        case 'string':
            if (filterData) {
                return {
                    label: columnLabel,
                    configs: [
                        {
                            type: 'select-many',
                            field: columnName,
                            items: filterData,
                            initialValue: initialValue,
                            op: 'in',
                        },
                    ],
                };
            }
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'string',
                        field: columnName,
                        label: columnLabel,
                        initialValue: initialValue,
                    },
                ],
            };
        case 'string-array':
            if (filterData) {
                return {
                    label: columnLabel,
                    configs: [
                        {
                            type: 'select-many',
                            field: columnName,
                            items: filterData,
                            op: 'overlap',
                            initialValue: initialValue,
                        },
                    ],
                };
            }
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'string',
                        field: columnName,
                        label: columnLabel,
                        mapToFilter: (filter) => {
                            const stringFilter = filter.find((f) => f.field === columnName);
                            let stringFilterValue = stringFilter?.value;
                            if (stringFilterValue && Array.isArray(stringFilterValue) && stringFilterValue.length) {
                                stringFilterValue = stringFilterValue[0];
                            }
                            if (typeof stringFilterValue !== 'string') {
                                return undefined;
                            }
                            return [{ column: columnName, op: 'overlap', value: [stringFilterValue] }];
                        },
                        initialValue: initialValue,
                    },
                ],
            };
        case 'boolean':
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'custom',
                        field: columnName,
                        component: (props) => (react_1.default.createElement(material_1.FormControlLabel, { sx: { marginTop: -1, marginBottom: -1 }, control: react_1.default.createElement(material_1.Switch, { checked: props.value === true, onChange: (event) => props.onChange(props.config.field, event.target.checked || undefined, undefined, undefined, columnLabel) }), label: undefined })),
                        initialValue: initialValue,
                    },
                ],
            };
        case 'number':
            if (dynamicColumn.metadata?.columnHandlerName.toLowerCase().includes('date')) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const initialDateFrom = (initialValues && initialValues.length > 0 && new Date(initialValues[0])) || undefined;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const initialDateTo = (initialValues && initialValues.length > 1 && new Date(initialValues[1])) || undefined;
                return {
                    label: columnLabel,
                    configs: [
                        {
                            type: 'date-from-to',
                            field: columnName,
                            fromLabel: 'From',
                            toLabel: 'To',
                            showChipLabel: true,
                            mapToFilter: (filter) => {
                                const dateFilter = filter.find((f) => f.field === columnName);
                                if (!dateFilter?.value) {
                                    return undefined;
                                }
                                const filterDates = dateFilter.value;
                                const filters = [];
                                if (filterDates.dateFrom) {
                                    filters.push({ column: columnName, op: '>=', value: filterDates.dateFrom.getTime() });
                                }
                                if (filterDates.dateTo) {
                                    filters.push({ column: columnName, op: '<=', value: filterDates.dateTo.getTime() });
                                }
                                if (filters.length) {
                                    return [{ op: 'and', filter: filters }];
                                }
                                return undefined;
                            },
                            initialValue: (initialDateFrom || initialDateTo) && {
                                dateFrom: initialDateFrom,
                                dateTo: initialDateTo,
                            },
                        },
                    ],
                };
            }
            else if (filterData) {
                return {
                    label: columnLabel,
                    configs: [
                        {
                            type: 'select-many',
                            field: columnName,
                            items: filterData,
                            op: 'in',
                            initialValue: initialValue,
                        },
                    ],
                };
            }
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'number',
                        field: columnName,
                        label: columnLabel,
                        initialValue: initialValue,
                    },
                ],
            };
        case 'number-array':
            if (filterData) {
                return {
                    label: columnLabel,
                    configs: [
                        {
                            type: 'select-many',
                            field: columnName,
                            items: filterData,
                            op: 'overlap',
                            initialValue: initialValue,
                        },
                    ],
                };
            }
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'number',
                        field: columnName,
                        label: columnLabel,
                        mapToFilter: (filter) => {
                            const numberFilter = filter.find((f) => f.field === columnName);
                            let numberFilterValue = numberFilter?.value;
                            if (numberFilterValue && Array.isArray(numberFilterValue) && numberFilterValue.length) {
                                numberFilterValue = numberFilterValue[0];
                            }
                            if (typeof numberFilterValue !== 'number') {
                                return undefined;
                            }
                            return [{ column: columnName, op: 'overlap', value: [numberFilterValue] }];
                        },
                        initialValue: initialValue,
                    },
                ],
            };
        case 'full-text-search':
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'string',
                        field: columnName,
                        label: columnLabel,
                        op: 'full-text-query',
                        initialValue: initialValue,
                    },
                ],
            };
        case 'id':
        case 'id-array':
            return {
                label: columnLabel,
                configs: [
                    {
                        type: 'select-many',
                        field: columnName,
                        items: filterData ?? [],
                        op: columnType === 'id' ? 'in' : 'overlap',
                        initialValue: initialValue,
                    },
                ],
            };
        default:
            throw Error(`Invalid dynamic column type: ${columnType}`);
    }
}
function mapHandlerToSelectItems(dynamicColumn, columnHandler) {
    if (!columnHandler?.data) {
        return undefined;
    }
    const selectItems = [];
    columnHandler.data.forEach((value, key) => {
        selectItems.push({
            label: value,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: key,
        });
    });
    return selectItems;
}
